import React, { useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import { Search } from "lucide-react";

function getSearchText(row, fields) {
  if (!fields?.length) return JSON.stringify(row || {}).toLowerCase();
  return fields
    .map((field) => {
      if (typeof field === "function") return field(row);
      return field.split(".").reduce((value, key) => value?.[key], row);
    })
    .filter((value) => value !== undefined && value !== null)
    .join(" ")
    .toLowerCase();
}

const tableStyles = {
  table: { style: { background: "transparent" } },
  tableWrapper: { style: { background: "transparent" } },
  header: { style: { background: "transparent", color: "var(--text)", padding: 0 } },
  subHeader: {
    style: {
      background: "rgba(255,255,255,.025)",
      borderBottom: "1px solid var(--line)",
      minHeight: 58,
      padding: "10px 16px",
    },
  },
  headRow: {
    style: {
      background: "rgba(255,255,255,.055)",
      borderBottom: "1px solid var(--line)",
      color: "var(--muted)",
      minHeight: 44,
    },
  },
  headCells: {
    style: {
      color: "var(--muted)",
      fontSize: 11,
      fontWeight: 800,
      letterSpacing: "1px",
      paddingLeft: 18,
      paddingRight: 18,
      textTransform: "uppercase",
    },
  },
  rows: {
    style: {
      background: "transparent",
      borderBottom: "1px solid var(--line)",
      color: "var(--text)",
      minHeight: 62,
      transition: "background .18s ease",
    },
    highlightOnHoverStyle: {
      background: "rgba(255,255,255,.035)",
      color: "var(--text)",
      outline: "none",
    },
  },
  cells: {
    style: {
      color: "var(--text)",
      fontSize: 14,
      paddingLeft: 18,
      paddingRight: 18,
    },
  },
  pagination: {
    style: {
      background: "rgba(255,255,255,.025)",
      borderTop: "1px solid var(--line)",
      color: "var(--muted)",
    },
    pageButtonsStyle: {
      borderRadius: 6,
      color: "var(--muted)",
      fill: "var(--muted)",
    },
  },
  noData: {
    style: {
      background: "transparent",
      color: "var(--muted)",
      minHeight: 150,
    },
  },
  progress: {
    style: {
      background: "transparent",
      color: "var(--muted)",
      minHeight: 150,
    },
  },
};

export default function SmartDataTable({
  columns,
  data,
  loading,
  searchFields,
  searchPlaceholder = "Search records...",
  emptyMessage = "No records found.",
  paginationPerPage = 10,
  showSearch = true,
  pagination = true,
  sortServer = false,
  onSort,
  defaultSortFieldId,
  defaultSortAsc = true,
}) {
  const [query, setQuery] = useState("");

  const filteredData = useMemo(() => {
    const items = Array.isArray(data) ? data : [];
    const needle = query.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((row) => getSearchText(row, searchFields).includes(needle));
  }, [data, query, searchFields]);

  const subHeader = (
    <label className="smart-table-search">
      <Search size={15} />
      <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={searchPlaceholder} />
    </label>
  );

  return (
    <div className="smart-table-wrap">
      <DataTable
        columns={columns}
        data={filteredData}
        progressPending={loading}
        progressComponent={<div className="smart-table-state">Loading records...</div>}
        noDataComponent={<div className="smart-table-state">{emptyMessage}</div>}
        customStyles={tableStyles}
        highlightOnHover
        pointerOnHover={false}
        responsive
        pagination={pagination}
        paginationPerPage={paginationPerPage}
        paginationRowsPerPageOptions={[10, 25, 50, 100]}
        persistTableHead
        subHeader={showSearch}
        subHeaderComponent={subHeader}
        sortServer={sortServer}
        onSort={onSort}
        defaultSortFieldId={defaultSortFieldId}
        defaultSortAsc={defaultSortAsc}
      />
    </div>
  );
}
